import React from 'react';
import { SettingsClient } from '../clients/SettingsClient';
import { ExtensionViewTypeValue } from '../components/Settings/ExtensionsPage/ExtensionViewType';
import { URLValidationRegex } from '../constants';

export const useExtensionFormFieldsHandlers = (
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  handleChange: (e: React.ChangeEvent<any>) => void,
  formValues: any,
  formErrors: any,
  formTouched: any,
) => {
  // in edit module form, embed code & embed link are under fields level.
  // however on extension connect form, they are directly on top object level.
  const embedCodeFieldName = formValues.fields
    ? `fields.embedCode`
    : 'embedCode';
  const embedLinkFieldName = formValues.fields
    ? `fields.embedLink`
    : 'embedLink';

  const viewTypeFieldName = 'viewType';
  const contentFieldName = formValues.fields ? 'fields.content' : 'content';

  const touched = formValues.fields ? formTouched.fields : formTouched;
  const values = formValues.fields ? formValues.fields : formValues;
  const errors = formValues.fields ? formErrors.fields : formErrors;

  const [, setValidatingEmbedCode] = React.useState(false);
  const fieldError = (fieldName: string) =>
    // when checking for errors, we need to check if `content` is touched.
    // that input is shared between embedCode and link which have specific properties in the `errors` object.
    touched && errors && touched.content && errors[fieldName];

  /**
   * when embed code changes check if it is url
   * if it is url then validate if returns embedly data
   * and use that for rendering
   */
  const validateEmbedCode = async (embedCode: string) => {
    setFieldValue(embedCodeFieldName, embedCode);
    setFieldValue('embedlyData', null);

    const urlRegex = new RegExp(URLValidationRegex);
    if (!embedCode.match(urlRegex)) {
      // if embedCode is not url dont validate embedCode
      return;
    }

    setValidatingEmbedCode(true);
    try {
      const result = await SettingsClient.validateExtension(embedCode);
      setFieldValue('embedlyData', result);
    } catch (ex) {
      setFieldValue('embedlyData', null);
    }
    setValidatingEmbedCode(false);
  };

  /**
   * This function calculates the content input error text
   * @returns
   */
  const contentFieldHelperText = () => {
    // when content field has error show it
    if (fieldError(contentFieldName)) {
      return formErrors.content || formErrors.fields.content;
    }
    // when view type field is link and embedLink field has error show it
    if (
      values[viewTypeFieldName] === ExtensionViewTypeValue.LINK &&
      fieldError('embedLink')
    ) {
      return fieldError('embedLink');
    }
    // when view type is embed and embedCode field has error show it
    if (
      values[viewTypeFieldName] === ExtensionViewTypeValue.EMBED &&
      fieldError('embedCode')
    ) {
      return fieldError('embedCode');
    }

    return '';
  };

  const updateEmbedContentFields = (
    ev: React.ChangeEvent<HTMLInputElement>,
    viewType: string,
    contentValue: string,
  ) => {
    handleChange(ev);
    // when view type is link, we need to put the content field value in the embedLink field
    // and clear out the embed code field.
    if (viewType === ExtensionViewTypeValue.LINK) {
      setFieldValue(embedLinkFieldName, contentValue);
      setFieldValue(embedCodeFieldName, '');
    } else {
      // otherwise, we need to put the content field value in the embedCode field
      // and clear out the embed link field.
      setFieldValue(embedLinkFieldName, '');
      validateEmbedCode(contentValue);
    }
  };

  const handleEmbedContentInputChange = (
    ev: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateEmbedContentFields(ev, values[viewTypeFieldName], ev.target.value);
  };

  const handleViewTypeChanged = (
    ev: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    updateEmbedContentFields(ev, value, values.content);
  };

  return {
    handleEmbedContentInputChange,
    handleViewTypeChanged,
    contentFieldHelperText,
  };
};
